/**
 * Every action the server writes to `partner_audit_log`, and how the Audit log
 * screen says each one in words.
 *
 * The list is transcribed from the server's `recordPartnerAudit({ action })`
 * calls (and the two raw INSERTs: `agent.refusal_alert`, and the
 * `pending_change.expire` sweep; `pending_change.apply` / `.fail` are the
 * worker applying an agent's delayed change, migration 074). `catalog.test.ts` scans server/src for
 * action strings and fails when one is missing here, so the Action filter
 * cannot silently lose an entry again (it lacked `terms.accept`). The screen
 * also adds any action it sees in the loaded log that this list does not
 * know, so even a server that ships first never hides a row from the filter.
 *
 * `describeAudit` turns an entry into one sentence ("Published widget settings
 * v2 (test key)") from its action, resource and before/after payloads. IDs a
 * reader may want (a lead, a key) are returned separately for a secondary
 * line; the raw resource path and HTTP method are NOT — they are in Details
 * only (third live review, 2026-09-27: "Refused an AI agent request (PUT
 * /partner/sites/fx_live_…/config/draft): … — scope.modes.live" now reads
 * "Claude Code tried to change Live conversation. Only a person can change
 * this.").
 */
import type { AuditEntry, PartnerRole } from '../api/types';
import { ROLE_LABELS } from '../auth/roles';
import { humanize } from '../ui/kit';
import { legalVersionLabel } from '../legal/current';

export interface AuditActionDef {
  action: string;
  /** Filter label: what happened, as a short noun phrase. */
  label: string;
  group: AuditGroup;
}

/**
 * The Action filter's `<optgroup>`s, in this order. Six, by who does it and
 * where (second audit, 2026-09-27: thirteen groups made ~60 entries hard to
 * scan): the partner's setup, lead handling, the team, AI agents, the
 * account, and FinderlyFix operator actions (approvals, plan flags, data
 * return and deletion) on their own at the end.
 */
export const AUDIT_GROUPS = ['Setup', 'Leads', 'Team', 'AI agents', 'Account', 'FinderlyFix operator'] as const;
export type AuditGroup = (typeof AUDIT_GROUPS)[number];

export const AUDIT_ACTIONS: AuditActionDef[] = [
  { action: 'org.create', label: 'Organisation created', group: 'Account' },
  { action: 'org.update', label: 'Organisation settings changed', group: 'Account' },
  { action: 'terms.accept', label: 'Partner Terms accepted', group: 'Account' },
  { action: 'org.close_request', label: 'Asked FinderlyFix to close the organisation', group: 'Account' },
  // Agent sign-up (migration 080).
  { action: 'signup.proof', label: 'AI agent proved the website at sign-up', group: 'AI agents' },
  { action: 'org.claim', label: 'Account claimed from an AI agent sign-up', group: 'Account' },
  { action: 'signup.reported', label: 'AI agent sign-up reported (“this wasn’t us”)', group: 'Account' },
  { action: 'signup.claim_reminder', label: 'Claim reminder sent', group: 'Account' },
  { action: 'signup.unclaimed_disable', label: 'Keys switched off: account unclaimed', group: 'Account' },
  { action: 'origin.verification_released', label: 'Website verification released (abandoned sign-up)', group: 'Setup' },
  { action: 'member.invite', label: 'Member invited', group: 'Team' },
  { action: 'member.invite_sent', label: 'Invitation emailed', group: 'Team' },
  { action: 'member.invite_accept', label: 'Invitation accepted', group: 'Team' },
  { action: 'member.invite_revoke', label: 'Invitation revoked', group: 'Team' },
  { action: 'member.role_change', label: 'Member role changed', group: 'Team' },
  { action: 'member.revoke', label: 'Member removed', group: 'Team' },
  { action: 'member.email_change', label: 'Member changed their sign-in email', group: 'Team' },
  { action: 'member.google_unlink', label: 'Member unlinked Google sign-in', group: 'Team' },
  { action: 'member.google_link', label: 'Member linked a Google account', group: 'Team' },
  { action: 'member.google_link_revert', label: 'Member undid a Google link (“This wasn’t me”)', group: 'Team' },
  { action: 'member.email_revert', label: 'Member undid a sign-in email change (“This wasn’t me”)', group: 'Team' },
  // Migration 093: who gets the AI agent and security notices.
  { action: 'member.notice_prefs', label: 'Member changed which AI agent notices they get', group: 'Team' },
  { action: 'notice_recipient.add', label: 'Notice address added', group: 'Account' },
  { action: 'notice_recipient.resend', label: 'Notice address confirmation resent', group: 'Account' },
  { action: 'notice_recipient.confirm', label: 'Notice address confirmed', group: 'Account' },
  { action: 'notice_recipient.remove', label: 'Notice address removed', group: 'Account' },
  { action: 'notice_recipient.unsubscribe', label: 'Notice address unsubscribed', group: 'Account' },
  { action: 'site.create', label: 'Site created', group: 'Setup' },
  { action: 'site.update', label: 'Key website policy changed', group: 'Setup' },
  { action: 'site.rename', label: 'Site renamed', group: 'Setup' },
  { action: 'site.rotate', label: 'Site key rotated', group: 'Setup' },
  { action: 'site.pause', label: 'Widget paused', group: 'Setup' },
  { action: 'site.resume', label: 'Widget resumed', group: 'Setup' },
  { action: 'site.verify', label: 'Install checked', group: 'Setup' },
  { action: 'site.network_fallback', label: 'Network fallback switched', group: 'Setup' },
  { action: 'network_fallback.request', label: 'Network fallback requested', group: 'Setup' },
  { action: 'network_fallback.cancel', label: 'Network fallback request withdrawn', group: 'Setup' },
  { action: 'network_fallback.approve', label: 'Network fallback request approved', group: 'FinderlyFix operator' },
  { action: 'network_fallback.decline', label: 'Network fallback request declined', group: 'FinderlyFix operator' },
  { action: 'widget_limits.request', label: 'Higher usage limits requested', group: 'Setup' },
  { action: 'widget_limits.cancel', label: 'Usage-limit request withdrawn', group: 'Setup' },
  { action: 'widget_limits.approve', label: 'Usage-limit request approved', group: 'FinderlyFix operator' },
  { action: 'widget_limits.decline', label: 'Usage-limit request declined', group: 'FinderlyFix operator' },
  { action: 'origin.add', label: 'Website added', group: 'Setup' },
  { action: 'origin.block', label: 'Website blocked', group: 'Setup' },
  { action: 'origin.unblock', label: 'Website unblocked', group: 'Setup' },
  { action: 'origin.remove', label: 'Website removed', group: 'Setup' },
  { action: 'config.save', label: 'Widget settings draft saved', group: 'Setup' },
  { action: 'config.publish', label: 'Widget settings published', group: 'Setup' },
  { action: 'config.discard_draft', label: 'Widget settings draft discarded', group: 'Setup' },
  { action: 'destination.create', label: 'Destination added', group: 'Setup' },
  { action: 'destination.update', label: 'Destination changed', group: 'Setup' },
  { action: 'destination.rotate_secret', label: 'Signing secret rotated', group: 'Setup' },
  { action: 'destination.test', label: 'Test event sent', group: 'Setup' },
  { action: 'lead.delivery_resend', label: 'Lead delivery resent', group: 'Leads' },
  { action: 'lead.reassign', label: 'Lead reassigned', group: 'Leads' },
  { action: 'lead.close', label: 'Lead closed', group: 'Leads' },
  { action: 'lead.reopen', label: 'Lead reopened', group: 'Leads' },
  { action: 'lead.mark_test', label: 'Lead marked as a test (FinderlyFix)', group: 'Leads' },
  { action: 'lead.media_added', label: 'Homeowner added a photo', group: 'Leads' },
  { action: 'dashboard.export', label: 'Leads exported (CSV)', group: 'Leads' },
  { action: 'dashboard.share_link.create', label: 'Stats link created', group: 'Leads' },
  { action: 'dashboard.share_link.revoke', label: 'Stats link revoked', group: 'Leads' },
  { action: 'hosted_page.create', label: 'Widget page created', group: 'Setup' },
  { action: 'hosted_page.update', label: 'Widget page changed', group: 'Setup' },
  { action: 'hosted_page.remove', label: 'Widget page removed', group: 'Setup' },
  { action: 'hosted_page.profile_update', label: 'Widget page profile changed', group: 'Setup' },
  { action: 'hosted_page.asset_add', label: 'Widget page photo or logo added', group: 'Setup' },
  { action: 'hosted_page.asset_update', label: 'Widget page photo details changed', group: 'Setup' },
  { action: 'hosted_page.asset_remove', label: 'Widget page photo or logo removed', group: 'Setup' },
  { action: 'lead.accept', label: 'Lead accepted (fee charged)', group: 'Leads' },
  { action: 'quote.create', label: 'Quote drafted', group: 'Leads' },
  { action: 'quote.update', label: 'Quote changed', group: 'Leads' },
  { action: 'quote.send', label: 'Quote sent to the homeowner', group: 'Leads' },
  { action: 'quote.withdraw', label: 'Quote withdrawn', group: 'Leads' },
  { action: 'project.resolve', label: 'Job dispute resolved (FinderlyFix)', group: 'Leads' },
  { action: 'billing.fee_schedule_accept', label: 'Contractor Fee Schedule accepted', group: 'Account' },
  { action: 'billing.contact_update', label: 'Billing contact changed', group: 'Account' },
  { action: 'billing.entry_posted', label: 'Statement line posted', group: 'Account' },
  { action: 'billing.account_update', label: 'Billing account changed (FinderlyFix)', group: 'FinderlyFix operator' },
  { action: 'billing.invoice_draft', label: 'Invoice drafted (FinderlyFix)', group: 'FinderlyFix operator' },
  { action: 'billing.invoice_issue', label: 'Invoice issued (FinderlyFix)', group: 'FinderlyFix operator' },
  { action: 'billing.invoice_paid', label: 'Invoice marked paid (FinderlyFix)', group: 'FinderlyFix operator' },
  { action: 'billing.invoice_void', label: 'Invoice voided (FinderlyFix)', group: 'FinderlyFix operator' },
  { action: 'routing.update', label: 'Routing settings saved', group: 'Setup' },
  { action: 'routing.rules.replace', label: 'Routing rules saved', group: 'Setup' },
  { action: 'vendor.create', label: 'Contractor added', group: 'Setup' },
  { action: 'vendor.update', label: 'Contractor changed', group: 'Setup' },
  { action: 'vendor.disable', label: 'Contractor disabled', group: 'Setup' },
  { action: 'vendor.import', label: 'Contractors imported', group: 'Setup' },
  { action: 'vendor.bulk', label: 'Contractors enabled or disabled in bulk', group: 'Setup' },
  { action: 'vendor.activate_attested', label: 'Contractor contact rules confirmed', group: 'Setup' },
  { action: 'vendor.opt_out', label: 'Contractor opted out', group: 'Setup' },
  { action: 'vendor.reconsent', label: 'Contractor opt-out lifted', group: 'Setup' },
  { action: 'vendor.resubscribe_link_sent', label: 'Contractor re-subscribe link sent', group: 'Setup' },
  { action: 'agent_token.create', label: 'AI agent token created', group: 'AI agents' },
  { action: 'agent_token.revoke', label: 'AI agent token revoked', group: 'AI agents' },
  { action: 'agent_token.renew', label: 'AI agent token renewed', group: 'AI agents' },
  { action: 'agent_token.revoke_self', label: 'AI agent revoked its own token', group: 'AI agents' },
  { action: 'agent.call', label: 'AI agent request', group: 'AI agents' },
  { action: 'agent.refused', label: 'AI agent request refused', group: 'AI agents' },
  { action: 'agent.refusal_alert', label: 'Repeated AI agent refusals alerted', group: 'AI agents' },
  { action: 'agent.test_lead', label: 'AI agent test lead', group: 'AI agents' },
  { action: 'pending_change.request', label: 'AI agent change waiting for approval', group: 'AI agents' },
  { action: 'pending_change.approve', label: 'AI agent change approved', group: 'AI agents' },
  { action: 'pending_change.apply', label: 'AI agent change took effect', group: 'AI agents' },
  { action: 'pending_change.undo', label: 'AI agent change undone', group: 'AI agents' },
  { action: 'pending_change.keep', label: 'AI agent change kept', group: 'AI agents' },
  { action: 'pending_change.fail', label: 'AI agent change failed', group: 'AI agents' },
  { action: 'pending_change.reject', label: 'AI agent change rejected', group: 'AI agents' },
  { action: 'pending_change.expire', label: 'AI agent change expired', group: 'AI agents' },
  { action: 'partner.approve', label: 'Organisation approved', group: 'FinderlyFix operator' },
  { action: 'partner.disable', label: 'Organisation suspended', group: 'FinderlyFix operator' },
  { action: 'partner.site_release', label: 'Website released for new sign-ups', group: 'FinderlyFix operator' },
  { action: 'partner.plan_flags', label: 'Plan settings changed', group: 'FinderlyFix operator' },
  { action: 'config.routing_actions', label: 'Widget routing actions changed', group: 'FinderlyFix operator' },
  // Operator-run return/deletion of the organisation's data (routes/admin/partner-offboarding.ts).
  { action: 'offboarding.open', label: 'Data return and deletion opened', group: 'FinderlyFix operator' },
  { action: 'offboarding.refreeze', label: 'Data return and deletion re-frozen', group: 'FinderlyFix operator' },
  { action: 'offboarding.export', label: 'Data export produced', group: 'FinderlyFix operator' },
  { action: 'offboarding.export_download', label: 'Data export downloaded', group: 'FinderlyFix operator' },
  { action: 'offboarding.dry_run', label: 'Deletion dry run', group: 'FinderlyFix operator' },
  { action: 'offboarding.execute_refused', label: 'Deletion refused', group: 'FinderlyFix operator' },
  { action: 'offboarding.execute', label: 'Deletion started', group: 'FinderlyFix operator' },
  { action: 'offboarding.advance', label: 'Deletion advanced', group: 'FinderlyFix operator' },
  { action: 'offboarding.cancel', label: 'Data return and deletion cancelled', group: 'FinderlyFix operator' },
  { action: 'offboarding.certificate', label: 'Deletion certificate issued', group: 'FinderlyFix operator' },
];

const BY_ACTION = new Map(AUDIT_ACTIONS.map((a) => [a.action, a]));

/**
 * The filter's options, grouped: the catalogue plus anything in `seen` it does
 * not know (a newer server), under "Other".
 */
export function filterGroups(seen: Iterable<string> = []): Array<{ group: string; actions: AuditActionDef[] }> {
  const groups = new Map<string, AuditActionDef[]>(AUDIT_GROUPS.map((g) => [g, []]));
  for (const a of AUDIT_ACTIONS) groups.get(a.group)!.push(a);
  const extra = [...new Set(seen)].filter((a) => !BY_ACTION.has(a)).sort();
  if (extra.length) groups.set('Other', extra.map((action) => ({ action, label: humanize(action), group: 'FinderlyFix operator' as const })));
  return [...groups].map(([group, actions]) => ({ group, actions }));
}

// ── Sentences ──────────────────────────────────────────────────────────────

export interface DescribeContext {
  /** A partner user's email or name, when known. */
  member?: (userId: number) => string | null;
  /** A site key's label, when it is one of this org's current sites. */
  siteLabel?: (siteKey: string) => string | null;
}

export interface AuditDescription {
  sentence: string;
  /** IDs worth keeping visible but secondary (the resource, a key). */
  ids: string[];
}

/** `member.notice_prefs` (migration 093): the choice, in the words of Your account. */
const NOTICE_MODE_WORDS: Record<string, string> = {
  all: '“Every notice”',
  digest: '“Daily digest only”',
  security: '“Security only”',
};
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : null);
const role = (v: unknown): string => (typeof v === 'string' && v in ROLE_LABELS ? ROLE_LABELS[v as PartnerRole] : humanize(String(v ?? 'member')));

export function hostOf(url: unknown): string | null {
  const s = str(url);
  if (!s) return null;
  try {
    return new URL(s).host;
  } catch {
    return s.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  }
}

function keyEnv(siteKey: string | null): string | null {
  if (!siteKey) return null;
  if (siteKey.startsWith('fx_test_')) return 'test key';
  if (siteKey.startsWith('fx_live_')) return 'live key';
  return null;
}

/** `widget_configs/fx_test_…/2` → ['fx_test_…', 2]. */
function configRef(resource: string): { siteKey: string | null; version: number | null } {
  const m = /^widget_configs\/([^/]+)\/(\d+)$/.exec(resource);
  return m ? { siteKey: m[1] ?? null, version: Number(m[2]) } : { siteKey: null, version: null };
}

function siteName(siteKey: string | null, ctx: DescribeContext): string | null {
  if (!siteKey) return null;
  return ctx.siteLabel?.(siteKey) ?? null;
}

function keyPhrase(siteKey: string | null, ctx: DescribeContext): string {
  const env = keyEnv(siteKey);
  const name = siteName(siteKey, ctx);
  if (env && name) return `${name}, ${env}`;
  return env ?? name ?? 'a site key';
}

function destinationTarget(d: Record<string, unknown> | null): string | null {
  if (!d) return null;
  const config = (d.config ?? {}) as Record<string, unknown>;
  if (d.kind === 'webhook') return hostOf(config.url) ?? 'a webhook';
  if (Array.isArray(config.recipients) && config.recipients.length) return config.recipients.map(String).join(', ');
  return null;
}

function destinationKind(d: Record<string, unknown> | null): string {
  if (d?.kind === 'webhook') return 'webhook';
  if (d?.kind === 'sms') return 'SMS';
  if (d?.kind === 'email') return 'email';
  return '';
}

const VERIFY_RESULT: Record<string, string> = {
  not_in_html: 'snippet not found',
  file_missing: 'ownership file not found',
  csp: 'blocked by the page’s security policy',
  blocked: 'website blocked',
  timeout: 'the page did not answer in time',
  error: 'the check failed',
  pending: 'still checking',
};

// ── AI agent requests, in words ────────────────────────────────────────────

/**
 * Setting and field names for the agent refusal sentences, by the name the
 * portal gives them. Covers the server's HUMAN_ONLY_CONFIG_RULES
 * (services/partner-agent-policy.ts; since migration 074 only
 * `branding.powered_by`), the refused body fields, and the fields that were
 * human-only before 074 so older audit rows still read well. An unknown path
 * falls back to its last segment, humanized.
 */
export const HUMAN_ONLY_FIELD_NAMES: Record<string, string> = {
  capture: 'the contact form and consent',
  routing: 'what visitors can do after the diagnosis',
  handoff: 'the FinderlyFix app handoff',
  'copy.disclaimer_md': 'the legal disclaimer',
  'copy.notice_at_collection_md': 'the notice at collection',
  'copy.audio_notice': 'the recording notice',
  'copy.live_notice': 'the recording notice',
  'scope.modes.live': 'Live conversation',
  'scope.modes.chat': 'Chat',
  'copy.live_notice_short': 'the short AI notice',
  'copy.chat_notice': 'the chat notice',
  'scope.live': 'the Live conversation camera',
  'scope.service_area.out_of_area_behavior': 'what happens to homeowners outside your area',
  'scope.out_of_scope_behavior': 'what happens to problems outside your trades',
  'safety.emergency_bypass': 'the emergency screen',
  'safety.emergency_phone': 'the emergency phone number',
  'safety.emergency_message_md': 'the emergency message',
  'branding.powered_by': '“Powered by FinderlyFix”',
  media_retention_days: 'how long photos and recordings are kept',
  do_not_contact: 'a contractor’s do-not-contact flag',
  active: 'whether a contractor is on',
  contact_rules_attested: 'the contractor contact-rules confirmation',
  escalation: 'the escalation contacts',
  config: 'where a lead destination sends leads',
  enabled: 'the Network fallback',
};

export function plainFieldName(path: string): string {
  const p = path.trim();
  if (HUMAN_ONLY_FIELD_NAMES[p]) return HUMAN_ONLY_FIELD_NAMES[p];
  const group = Object.keys(HUMAN_ONLY_FIELD_NAMES).find((k) => !k.includes('.') && p.startsWith(`${k}.`));
  if (group) return HUMAN_ONLY_FIELD_NAMES[group] as string;
  const last = p.split('.').pop() ?? p;
  return humanize(last.replace(/_md$/, '')).toLowerCase();
}

/** `a, b and c`, de-duplicated. */
function listWords(items: string[]): string {
  const unique = [...new Set(items)];
  if (unique.length <= 1) return unique[0] ?? '';
  return `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
}

/**
 * What an agent request was trying to do, from its `METHOD /partner/…`
 * resource: [to-infinitive, past tense]. Checked in order; the first match wins.
 */
const AGENT_ROUTE_WORDS: Array<[string, RegExp, string, string]> = [
  ['PUT', /\/config\/draft$/, 'save a widget settings draft', 'saved a widget settings draft'],
  ['POST', /\/config\/publish$/, 'publish widget settings', 'published widget settings'],
  ['GET', /\/config$/, 'read the widget settings', 'read the widget settings'],
  ['POST', /\/agent\/sites\/[^/]+\/test-lead$/, 'send a test lead', 'sent a test lead'],
  ['POST', /\/sites\/[^/]+\/verify$/, 'run an install check', 'ran an install check'],
  ['POST', /\/sites\/[^/]+\/csp-check$/, 'check a page’s security policy', 'checked a page’s security policy'],
  ['POST', /\/sites\/[^/]+\/rotate$/, 'rotate a site key', 'rotated a site key'],
  ['POST', /\/sites$/, 'create a site', 'created a site'],
  ['*', /\/sites\/[^/]+\/origins/, 'change where a site key works', 'changed where a site key works'],
  ['GET', /\/sites/, 'read your sites', 'read your sites'],
  ['POST', /\/destinations$/, 'add a lead destination', 'added a lead destination'],
  ['POST', /\/destinations\/[^/]+\/rotate-secret$/, 'rotate a webhook signing secret', 'rotated a webhook signing secret'],
  ['POST', /\/destinations\/[^/]+\/test$/, 'send a test event to a destination', 'sent a test event to a destination'],
  ['GET', /\/destinations\/[^/]+\/deliveries$/, 'read the delivery log', 'read the delivery log'],
  ['*', /\/destinations/, 'change a lead destination', 'changed a lead destination'],
  ['*', /\/leads/, 'read or act on leads', 'read leads'],
  ['*', /\/org\/members/, 'change your team', 'read your team'],
  ['POST', /\/terms\/accept$/, 'accept the Partner Terms', 'accepted the Partner Terms'],
  ['*', /\/org$/, 'change your organisation’s details', 'read your organisation’s details'],
  ['*', /\/dashboard\/export/, 'export lead data', 'exported lead data'],
  ['*', /\/dashboard\/share-link/, 'share the dashboard', 'shared the dashboard'],
  ['*', /\/audit$/, 'read the audit log', 'read the audit log'],
  ['POST', /\/vendors\/bulk$/, 'turn contractors on', 'turned contractors on or off'],
  ['DELETE', /\/vendors\//, 'turn a contractor off', 'turned a contractor off'],
  ['POST', /\/vendors\/import$/, 'import contractors', 'imported contractors'],
  ['*', /\/vendors/, 'change a contractor', 'changed a contractor'],
  ['*', /\/routing/, 'change routing', 'changed routing'],
  ['*', /\/agent-tokens/, 'manage AI agent tokens', 'managed AI agent tokens'],
  ['*', /\/pending-changes/, 'decide on an AI agent’s change', 'made or asked for a change'],
  ['*', /\/agent-change-links/, 'decide on an AI agent’s change', 'decided on an AI agent’s change'],
  ['*', /\/hosted-page/, 'change the hosted page', 'changed the hosted page'],
  ['GET', /\/setup-progress$/, 'read your setup progress', 'read your setup progress'],
];

function agentRouteWords(resource: string): [string, string] {
  const m = /^([A-Z]+)\s+(\S+)/.exec(resource);
  const method = m?.[1] ?? '';
  const path = (m?.[2] ?? resource).split('?')[0] ?? '';
  for (const [verb, re, inf, past] of AGENT_ROUTE_WORDS) {
    if ((verb === '*' || verb === method) && re.test(path)) return [inf, past];
  }
  return method === 'GET' ? ['read something in your account', 'read something in your account'] : ['change something in your account', 'changed something in your account'];
}

/** The agent by its token's label ("Claude Code"), or "An AI agent". */
function agentName(e: AuditEntry): string {
  return str(e.actor_agent_token_label) ?? 'An AI agent';
}

function refusalSentence(e: AuditEntry, a: Record<string, unknown>): string {
  const who = agentName(e);
  const [inf] = agentRouteWords(e.resource);
  switch (String(a.reason ?? '')) {
    case 'human_only_field': {
      const fields = (str(a.field) ?? '').split(',').map((f) => f.trim()).filter(Boolean);
      if (!fields.length) return `${who} tried to change a setting only a person can change.`;
      return `${who} tried to change ${listWords(fields.map(plainFieldName))}. Only a person can change ${fields.length > 1 ? 'these' : 'this'}.`;
    }
    case 'human_only':
      // Partner Terms §2: an agent may accept a later version once the
      // organisation has a first acceptance (a person's in the portal, or the
      // one made at agent sign-up, migration 080).
      if (/\/terms\/accept$/.test(e.resource)) {
        return `${who} tried to accept the Partner Terms for an organisation that has never accepted them. The first acceptance is made by a person in the portal (or at agent sign-up).`;
      }
      return `${who} tried to ${inf}. Only a person can do this.`;
    case 'unclaimed':
      // AGENT_CLAIM_REQUIRED (migration 080).
      return `${who} tried to ${inf}. That waits until the person it named claims the account.`;
    case 'delayed':
      return `${who} tried to ${inf} directly. It makes this change another way, which emails owners and admins with an Undo.`;
    case 'not_allowed':
      return `${who} tried to ${inf}. AI agents can’t do this.`;
    case 'scope':
      return `${who} tried to ${inf}, but its token can’t change widget settings.`;
    case 'site_scope':
      return `${who} tried to ${inf} on a site its token isn’t allowed to use.`;
    default:
      return `${who} tried to ${inf}, and was refused.`;
  }
}

const ORG_FIELD: Record<string, string> = {
  display_name: 'name',
  partner_type: 'partner type',
  media_retention_days: 'photo retention',
};

function changedKeys(before: Record<string, unknown> | null, after: Record<string, unknown> | null): string[] {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  return [...keys].filter((k) => JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k]));
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** One sentence for an audit entry. Unknown actions fall back to their humanized code. */
export function describeAudit(e: AuditEntry, ctx: DescribeContext = {}): AuditDescription {
  const a = (e.after ?? {}) as Record<string, unknown>;
  const b = (e.before ?? {}) as Record<string, unknown>;
  // The raw resource is in Details only (see the header).
  const ids: string[] = [];
  const member = (id: unknown) => {
    const n = num(id);
    return (n !== null ? ctx.member?.(n) : null) ?? (n !== null ? `member #${n}` : 'a member');
  };
  const say = (sentence: string, extraIds: Array<string | null> = []): AuditDescription => ({
    sentence,
    ids: [...ids, ...extraIds.filter((x): x is string => Boolean(x))],
  });

  switch (e.action) {
    case 'org.create':
      if (a.created_by_agent) {
        // Migration 080: an AI agent signed the organisation up for a named person.
        return say(
          `${agentName(e)} created the organisation${str(a.display_name) ? ` “${String(a.display_name)}”` : ''} at sign-up${
            str(a.contact_email) ? `, naming ${String(a.contact_email)} as the person who authorised it` : ''
          }`,
        );
      }
      return say(str(a.display_name) ? `Created the organisation “${String(a.display_name)}”` : 'Created the organisation');
    case 'signup.proof': {
      const client = (a.agent_client ?? {}) as Record<string, unknown>;
      const agent = [str(client.name), str(client.version)].filter(Boolean).join(' ') || agentName(e);
      const how = a.proof === 'dns' ? 'a DNS TXT record' : 'a verification file';
      return say(`${agent} proved control of ${hostOf(a.origin) ?? str(a.host) ?? 'the website'} with ${how} to sign up`);
    }
    case 'org.close_request':
      return say(
        `Asked FinderlyFix to close the organisation and delete its data${str(a.reason) ? `: “${String(a.reason)}”` : ''}`,
      );
    case 'org.claim':
      return say(
        a.keys_reenabled
          ? 'Confirmed and took ownership of the account an AI agent set up; the widget keys work again'
          : 'Confirmed and took ownership of the account an AI agent set up',
      );
    case 'signup.reported':
      return say(
        `${str(a.reported_by) ?? 'The person the AI agent named'} reported that they did not authorise this account: widget keys switched off${
          num(a.agent_tokens_revoked) ? `, ${plural(num(a.agent_tokens_revoked) ?? 0, 'AI agent token')} revoked` : ''
        }, flagged for FinderlyFix`,
      );
    case 'origin.verification_released':
      return say(
        `${hostOf(a.origin) ?? 'A website'} is no longer verified here: nobody claimed this account within 14 days, and another account proved control of the website`,
      );
    case 'signup.claim_reminder':
      return say(`Reminded the named person to claim the account (reminder ${num(a.reminder) ?? 1}, day ${num(a.day) ?? '?'})`);
    case 'signup.unclaimed_disable':
      return say(`Switched the widget keys off: nobody claimed the account within ${num(a.claim_window_days) ?? 14} days (nothing deleted)`);
    case 'org.update': {
      const fields = changedKeys(e.before, e.after).filter((k) => k in ORG_FIELD).map((k) => ORG_FIELD[k]);
      return say(fields.length ? `Changed the organisation’s ${fields.join(', ')}` : 'Updated the organisation');
    }
    case 'terms.accept': {
      const version = str(a.terms_version) ? ` (${legalVersionLabel(String(a.terms_version))})` : '';
      if (a.method === 'agent_attested') {
        // Migration 080: the organisation's first acceptance, at agent sign-up.
        const c = (a.contact ?? {}) as Record<string, unknown>;
        const on = [str(c.name), str(c.role)].filter(Boolean).join(', ');
        return say(`${agentName(e)} accepted the Partner Terms${version} for the organisation at sign-up${on ? `, on the authority of ${on}` : ''}`);
      }
      if (a.method === 'claim_confirmed') return say(`Accepted the Partner Terms${version} when claiming the account`);
      // Partner Terms §2 (2026-09-27): an agent may accept a LATER version.
      if (a.accepted_via_agent) {
        return say(`${agentName(e)} accepted the updated Partner Terms${version}${str(a.organisation) ? ` for ${String(a.organisation)}` : ''}`);
      }
      return say(`Accepted the Partner Terms${version}`);
    }
    case 'member.invite':
      return say(`Invited ${str(a.email) ?? member(a.user_id)} as ${role(a.role)}`);
    // Migration 083: an AI agent's invitation is emailed, and becomes a
    // membership only when the invitee accepts it.
    case 'member.invite_sent':
      return say(
        `Emailed ${str(a.email) ?? 'someone'} an invitation to join as ${role(a.role)}, ${
          a.applied_after_window ? 'an AI agent’s invitation that took effect after its window' : 'an AI agent’s invitation an owner or admin approved'
        }; they join only if they accept it`,
      );
    case 'member.invite_accept':
      return say(`${str(a.email) ?? member(a.user_id)} accepted an invitation and joined as ${role(a.role)}`);
    case 'member.invite_revoke':
      return say(`Revoked the invitation for ${str(a.email) ?? 'someone'} (${role(a.role)})`);
    case 'member.role_change':
      return say(`Changed ${member(a.user_id ?? b.user_id)} from ${role(b.role)} to ${role(a.role)}`);
    case 'member.revoke':
      return say(`Removed ${member(b.user_id)} (${role(b.role)}) from the team`);
    case 'member.email_change':
      return say(
        str(b.email) && str(a.email)
          ? `Changed their sign-in email from ${String(b.email)} to ${String(a.email)}`
          : 'Changed their sign-in email',
      );
    case 'member.notice_prefs':
      return say(`Chose ${NOTICE_MODE_WORDS[String(a.agent_notices)] ?? 'a setting'} for AI agent notices`);
    case 'notice_recipient.add':
      return say(`Added ${str(a.email) ?? 'an address'} for AI agent and security notices (waiting for it to confirm)`);
    case 'notice_recipient.resend':
      return say(`Sent ${str(a.email) ?? 'an address'} a new confirmation link for AI agent and security notices`);
    case 'notice_recipient.confirm':
      return say(`${str(a.email) ?? 'An address'} confirmed it wants AI agent and security notices`);
    case 'notice_recipient.remove':
      return say(`Removed ${str(b.email) ?? 'an address'} from AI agent and security notices`);
    case 'notice_recipient.unsubscribe':
      return say(`${str(b.email) ?? 'An address'} stopped AI agent and security notices with its own link`);
    case 'member.google_unlink':
      return say('Unlinked Google sign-in from their account');
    case 'member.google_link':
      return say(str(a.google_email) ? `Linked a Google account (${String(a.google_email)}) to sign in` : 'Linked a Google account to sign in');
    case 'member.google_link_revert':
      return say('Undid a Google link from their sign-in email (“This wasn’t me”) and signed out everywhere');
    case 'member.email_revert':
      return say(
        str(b.email) && str(a.email)
          ? `Undid a sign-in email change from the old address (“This wasn’t me”): ${String(b.email)} back to ${String(a.email)}, signed out everywhere`
          : 'Undid a sign-in email change from the old address (“This wasn’t me”) and signed out everywhere',
      );
    case 'site.create':
      return say(`Created the site “${str(a.label) ?? 'untitled'}” with a live and a test key`, [str(a.test_site_key)]);
    case 'site.rename':
      return say(`Renamed the site “${str(b.label) ?? 'untitled'}” to “${str(a.label) ?? 'untitled'}”`);
    case 'site.update': {
      const policy = a.origin_policy === 'listed' ? 'only the websites on its list' : 'any website';
      const key = e.resource.replace(/^widget_sites\//, '');
      return say(`Set the ${keyPhrase(key, ctx)} to work on ${policy}`);
    }
    case 'site.rotate': {
      const from = str(a.rotated_from) ?? str(b.site_key);
      return say(`Rotated the ${keyPhrase(from ?? str(a.site_key), ctx)}; the old key works for 7 days`, [from]);
    }
    case 'site.pause':
    case 'site.resume': {
      const key = e.resource.replace(/^widget_sites\//, '');
      const verb = e.action === 'site.pause' ? 'Paused' : 'Resumed';
      return say(`${verb} the widget on the ${keyPhrase(key || null, ctx)} and its test key`);
    }
    case 'site.verify': {
      const host = hostOf(a.url) ?? 'a page';
      return say(a.result === 'found' ? `Verified ${host}` : `Checked ${host}: ${VERIFY_RESULT[String(a.result)] ?? humanize(String(a.result ?? 'no result'))}`, [
        str(a.site_key),
      ]);
    }
    case 'site.network_fallback': {
      const label = str(a.site_label) ?? 'a site';
      const why = a.reason === 'partner_type_change' ? ' because the partner type changed to one without a network yet' : '';
      return say(a.enabled ? `Turned the network fallback on for ${label}` : `Turned the network fallback off for ${label}${why}`);
    }
    case 'network_fallback.request':
      return say(
        a.kind === 'notify'
          ? 'Asked to be told when a neutral network is available'
          : `Asked FinderlyFix to turn the network fallback on for ${str(a.site_label) ?? 'a site'}`,
      );
    case 'network_fallback.cancel':
      return say(str(a.reason) ? `Closed a network fallback request: ${String(a.reason)}` : 'Withdrew a network fallback request');
    case 'network_fallback.approve':
      return say(`Approved the network fallback for ${str(a.site_label) ?? 'a site'}`);
    case 'network_fallback.decline':
      return say(`Declined a network fallback request${str(a.reason) ? `: ${String(a.reason)}` : ''}`);
    case 'widget_limits.request':
      return say(`Asked FinderlyFix for higher widget usage limits on ${str(a.site_label) ?? 'a site'}`);
    case 'widget_limits.cancel':
      return say('Withdrew a request for higher widget usage limits');
    case 'widget_limits.approve':
      return say('FinderlyFix raised the widget usage limits');
    case 'widget_limits.decline':
      return say(`Declined a request for higher widget usage limits${str(a.reason) ? `: ${String(a.reason)}` : ''}`);
    case 'origin.add':
      if (a.via === 'signup_file' || a.via === 'signup_dns') {
        return say(
          `Added ${hostOf(a.origin) ?? 'the website'} at sign-up, ${
            a.verified ? 'verified by the file' : a.via === 'signup_dns' ? 'proven by a DNS record (the file check verifies it later)' : 'not verified yet'
          }`,
        );
      }
      return say(`Added ${hostOf(a.origin) ?? 'a website'} to the ${keyPhrase(str(a.site_key), ctx)}`);
    case 'origin.block':
      return say(`Blocked ${hostOf(a.origin) ?? 'a website'} from using the ${keyPhrase(str(a.site_key), ctx)}`);
    case 'origin.unblock':
      return say(`Unblocked ${hostOf(a.origin) ?? 'a website'} for the ${keyPhrase(str(a.site_key), ctx)}`);
    case 'origin.remove':
      return say(`Removed ${hostOf(b.origin) ?? 'a website'} from the ${keyPhrase(str(b.site_key), ctx)}`);
    case 'config.save': {
      const { siteKey, version } = configRef(e.resource);
      return say(`Saved widget settings draft${version ? ` v${version}` : ''} (${keyPhrase(siteKey, ctx)})`);
    }
    case 'config.publish': {
      const { siteKey, version } = configRef(e.resource);
      return say(`Published widget settings${version ? ` v${version}` : ''} (${keyPhrase(siteKey, ctx)})`);
    }
    case 'config.discard_draft': {
      const restored = num(a.restored_from);
      const discarded = num(b.draft_version);
      return say(
        `Discarded the widget settings draft${discarded ? ` v${discarded}` : ''}; back to what was live${restored ? ` (v${restored})` : ''} (${keyPhrase(configRef(e.resource).siteKey, ctx)})`,
      );
    }
    case 'config.routing_actions': {
      const { siteKey, version } = configRef(e.resource);
      return say(`Changed the widget’s routing actions${version ? `, v${version}` : ''} (${keyPhrase(siteKey, ctx)})`);
    }
    case 'destination.create': {
      const target = destinationTarget(a);
      const via = a.via_pending_change
        ? a.applied_after_window
          ? ', an AI agent’s change that took effect after its window'
          : a.applied_for_agent
            ? ', an AI agent’s change (owners were emailed with an Undo)'
            : ', applying an AI agent’s change'
        : '';
      const kind = destinationKind(a);
      const what = kind === 'email' ? 'an email destination' : kind ? `a ${kind} destination` : 'a destination';
      return say(`Added ${what}${target ? ` (${target})` : ''}${via}`);
    }
    case 'destination.update': {
      const target = destinationTarget(a) ?? destinationTarget(b);
      const suffix = target ? ` (${target})` : '';
      if (b.status !== a.status && a.status === 'disabled') return say(`Disabled a destination${suffix}`);
      if (b.status !== a.status && a.status === 'active') return say(`Turned a destination back on${suffix}`);
      return say(`Changed a destination${suffix}`);
    }
    case 'destination.rotate_secret':
      return say('Rotated a webhook’s signing secret; the previous one works for 24 hours');
    case 'destination.test':
      return say('Sent a test event to a destination');
    case 'lead.delivery_resend':
      return say(`Resent a lead delivery${num(a.attempt) ? ` (attempt ${String(a.attempt)})` : ''}`);
    case 'lead.reassign':
      return say(`Reassigned a lead to another contractor${str(a.reason) ? `: “${String(a.reason)}”` : ''}`, [str(a.vendor_id)]);
    case 'lead.close':
      return say(`Closed a lead as “${humanize(str(a.outcome) ?? 'closed')}”`);
    case 'lead.reopen':
      return say(`Reopened a lead${str(a.note) ? `: “${String(a.note)}”` : ''}`);
    case 'lead.mark_test':
      return say('FinderlyFix marked a lead as a test lead; it no longer counts on your dashboard');
    case 'lead.media_added':
      return say('The homeowner added a photo to their request after sending it');
    case 'hosted_page.profile_update':
      return say('Changed the public profile on a widget page');
    case 'hosted_page.asset_add':
      return say('Added a photo or logo to a widget page');
    case 'hosted_page.asset_update':
      return say('Changed a widget page photo’s description or order');
    case 'hosted_page.asset_remove':
      return say('Removed a photo or logo from a widget page');
    case 'lead.accept':
      return say('Accepted a lead in the portal; the lead fee is on the statement and the homeowner’s details are unlocked');
    case 'quote.create':
      return say('Drafted a quote');
    case 'quote.update':
      return say('Changed a quote');
    case 'billing.fee_schedule_accept':
      return say(`Accepted the Contractor Fee Schedule${str(a.version) ? ` (${String(a.version)})` : ''}`);
    case 'billing.contact_update':
      return say('Changed the billing contact');
    case 'billing.entry_posted':
      return say(`A ${humanize(str(a.kind) ?? 'statement')} line was posted to the statement`);
    case 'billing.account_update':
      return say('FinderlyFix changed the billing account (prices, fees or whether fees are on)');
    case 'billing.invoice_draft':
      return say('FinderlyFix drafted an invoice');
    case 'billing.invoice_issue':
      return say(`FinderlyFix issued an invoice${str(a.number) ? ` ${String(a.number)}` : ''}`);
    case 'billing.invoice_paid':
      return say('FinderlyFix marked an invoice as paid');
    case 'billing.invoice_void':
      return say('FinderlyFix voided an invoice');
    case 'quote.send':
      return say('Sent a quote to the homeowner');
    case 'quote.withdraw':
      return say(`Withdrew a quote${str(a.reason) ? `: “${String(a.reason)}”` : ''}`);
    case 'project.resolve':
      return say(`FinderlyFix resolved a job dispute as “${humanize(str(a.outcome) ?? 'resolved')}”`);
    case 'dashboard.export':
      return say(num(a.rows) !== null ? `Exported ${plural(num(a.rows) ?? 0, 'lead')} to CSV` : 'Exported leads to CSV');
    case 'dashboard.share_link.create': {
      const scope = (a.scope ?? {}) as Record<string, unknown>;
      const days = num(scope.period_days) ?? 30;
      return say(`Created a link to share your stats (last ${days} days${str(scope.origin) ? `, ${hostOf(scope.origin)}` : ''}${str(scope.trade) ? `, ${humanize(String(scope.trade))}` : ''})`);
    }
    case 'dashboard.share_link.revoke':
      return say('Revoked a link to your stats');
    case 'hosted_page.create':
      return say(`Created the widget page finderlyfix.com/p/${str(a.slug) ?? e.resource.replace(/^partner_hosted_pages\//, '')}`);
    case 'hosted_page.remove':
      return say(`Removed the widget page finderlyfix.com/p/${str(a.slug) ?? str(b.slug) ?? e.resource.replace(/^partner_hosted_pages\//, '')}`);
    case 'hosted_page.update': {
      if (b.enabled === false && a.enabled === true) return say('Turned the widget page back on');
      if (b.enabled === true && a.enabled === false) return say('Turned the widget page off');
      const fields = changedKeys(e.before, e.after).filter((k) => k === 'headline' || k === 'intro' || k === 'site_key');
      return say(fields.length ? `Edited the widget page’s ${fields.map((f) => (f === 'site_key' ? 'site key' : f)).join(' and ')}` : 'Edited the widget page');
    }
    case 'routing.update':
      return say(e.before ? 'Changed routing settings' : 'Saved routing settings for the first time');
    case 'routing.rules.replace': {
      const n = Array.isArray(a.rules) ? a.rules.length : null;
      return say(n === null ? 'Saved routing rules' : n === 0 ? 'Removed all routing rules' : `Saved ${plural(n, 'routing rule')}`);
    }
    case 'vendor.create':
      return say(`Added the contractor ${str(a.name) ?? ''}`.trim());
    case 'vendor.update':
      return say(`Changed the contractor ${str(a.name) ?? str(b.name) ?? ''}`.trim());
    case 'vendor.disable':
      return say('Disabled a contractor');
    case 'vendor.import': {
      const created = num(a.created) ?? 0;
      const updated = num(a.updated) ?? 0;
      const rejected = num(a.rejected) ?? 0;
      return say(`Imported contractors: ${created} added, ${updated} updated${rejected ? `, ${rejected} rejected` : ''}`);
    }
    case 'vendor.bulk': {
      const n = num(a.updated) ?? (Array.isArray(a.vendor_ids) ? a.vendor_ids.length : 0);
      return say(`${a.active === false ? 'Disabled' : 'Turned on'} ${plural(n, 'contractor')}`);
    }
    case 'vendor.activate_attested': {
      const n = Array.isArray(a.vendor_ids) ? a.vendor_ids.length : 1;
      return say(
        a.attested_via_agent
          ? `${agentName(e)} relayed your organisation’s confirmation of the contact rules for ${plural(n, 'contractor')}, and they were turned on`
          : `Confirmed the contact rules for ${plural(n, 'contractor')}`,
      );
    }
    case 'vendor.opt_out':
      return say(
        e.actor_operator === 'contractor'
          ? 'A contractor unsubscribed from job offers with their own link'
          : `Marked a contractor do not contact${a.channel === 'api' ? ' (through the API)' : ''}`,
        [str(a.vendor_id)],
      );
    case 'vendor.reconsent':
      if (e.actor_operator === 'contractor') return say('A contractor asked for job offers again with their own link', [str(a.vendor_id)]);
      return say(
        str(a.attestation) ? `Lifted a contractor’s opt-out: “${String(a.attestation)}”` : 'Lifted a contractor’s opt-out on their renewed consent',
        [str(a.vendor_id)],
      );
    case 'vendor.resubscribe_link_sent':
      return say(`Emailed ${str(a.name) ?? 'a contractor'} their own link to start job offers again`, [str(a.vendor_id)]);
    case 'agent_token.create':
      if (a.via === 'signup') return say(`Issued the AI agent token “${str(a.label) ?? 'untitled'}” to the agent that signed up`);
      return say(`Created the AI agent token “${str(a.label) ?? 'untitled'}”`);
    case 'agent_token.revoke': {
      const chain = Array.isArray(a.renewals_revoked) ? a.renewals_revoked.length : 0;
      return say(`Revoked the AI agent token “${str(a.label) ?? 'untitled'}”${chain ? ` and ${plural(chain, 'renewal')} of it` : ''}`);
    }
    case 'agent_token.revoke_self':
      return say(`${agentName(e)} revoked its own token “${str(a.label) ?? 'untitled'}”${str(a.reason) ? `: “${String(a.reason)}”` : ''}`);
    case 'agent_token.renew':
      return say(`${agentName(e)} renewed its token “${str(a.label) ?? 'untitled'}” (same access${str(a.expires_at) ? `, now until ${String(a.expires_at).slice(0, 10)}` : ''})`);
    case 'agent.call': {
      const status = num(a.status);
      const [inf, past] = agentRouteWords(e.resource);
      return say(status !== null && status >= 400 ? `${agentName(e)} tried to ${inf}; it didn’t go through` : `${agentName(e)} ${past}`);
    }
    case 'agent.refused':
      return say(refusalSentence(e, a));
    case 'agent.refusal_alert':
      return say(`Alerted owners and admins: an AI agent was refused ${num(a.refused) ?? 'several'} times in ${num(a.window_minutes) ?? 'a few'} minutes`);
    case 'agent.test_lead':
      return say('AI agent sent a test lead', [str(a.lead_id)]);
    case 'pending_change.request': {
      const mins = num(a.applies_in_minutes);
      return say(
        `${agentName(e)} asked for a change${str(a.summary) ? `: ${String(a.summary)}` : ''}. ${
          mins === null ? 'It waits for an owner or admin to approve it.' : `It takes effect in ${plural(mins, 'minute')} unless someone rejects it.`
        }`,
      );
    }
    case 'pending_change.approve':
      return say('Approved an AI agent’s change', [str(a.destination_id)]);
    case 'pending_change.apply':
      return say(
        a.applied_at_once
          ? 'An AI agent’s change took effect; owners and admins were emailed with an Undo link'
          : 'An AI agent’s change took effect: nobody rejected it during its window',
        [str(a.destination_id)],
      );
    case 'pending_change.undo':
      return say('Undid an AI agent’s change', [str(a.destination_id)]);
    case 'pending_change.keep':
      return say('Kept an AI agent’s change (off the dashboard banner)');
    case 'pending_change.fail':
      return say(`An AI agent’s change could not be applied${str(a.error) ? ` (${humanize(String(a.error).toLowerCase()).toLowerCase()})` : ''}`);
    case 'pending_change.reject':
      return say('Rejected an AI agent’s change');
    case 'pending_change.expire':
      return say('An AI agent’s change expired without a decision');
    case 'partner.approve':
      return say('Approved the organisation');
    case 'partner.disable':
      return say('Suspended the organisation');
    case 'partner.site_release':
      return say(`Released ${str(b.signup_host) ?? 'the organisation’s website'} so a new sign-up can prove it`);
    case 'partner.plan_flags':
      return say('Changed the plan settings');
    case 'offboarding.open':
      return say('Opened the return and deletion of your data; changes are frozen');
    case 'offboarding.refreeze':
      return say('Re-applied the freeze on the open data return and deletion');
    case 'offboarding.export':
      return say('Produced the export of your data');
    case 'offboarding.export_download':
      return say('Downloaded the export of your data');
    case 'offboarding.dry_run':
      return say(`Ran a deletion dry run${num(a.leads) !== null ? ` (${plural(num(a.leads) ?? 0, 'lead')})` : ''}`);
    case 'offboarding.execute_refused':
      return say(`Refused to start the deletion${str(a.reason) ? `: ${humanize(String(a.reason).toLowerCase())}` : ''}`);
    case 'offboarding.execute':
      return say('Started deleting your data');
    case 'offboarding.advance':
      return say('Moved the data deletion to its next step');
    case 'offboarding.cancel':
      return say('Cancelled the data return and deletion');
    case 'offboarding.certificate':
      return say('Issued the data deletion certificate');
    default:
      return say(humanize(e.action));
  }
}

// ── Details: before/after as rows ─────────────────────────────────────────

export interface DetailRow {
  field: string;
  before: string | null;
  after: string | null;
}

export function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))).join(', ') : 'none';
  return JSON.stringify(v);
}

/** One row per key in before ∪ after; `before`/`after` are null when that side has no such key. */
export function detailRows(e: AuditEntry): DetailRow[] {
  const keys = [...new Set([...Object.keys(e.before ?? {}), ...Object.keys(e.after ?? {})])];
  return keys.map((k) => ({
    field: humanize(k),
    before: e.before && k in e.before ? formatValue(e.before[k]) : null,
    after: e.after && k in e.after ? formatValue(e.after[k]) : null,
  }));
}
